import { ArrowLeft, ChevronDown, ChevronLeft, Edit, MessageCircle, RefreshCw, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function formatInquiryTime(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function ResourceDetail({ resource }) {
  const navigate = useNavigate();

  // ─── Inquiry state ──────────────────────────────────────────────────────────
  const [showInquiries, setShowInquiries] = useState(false);
  const [inquiryThreads, setInquiryThreads] = useState([]);
  const [isLoadingInquiries, setIsLoadingInquiries] = useState(false);
  const [inquiriesFetched, setInquiriesFetched] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // { userId, name }
  const [thread, setThread] = useState([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);

  if (!resource) {
    return (
      <div className="w-[90%] lg:w-[95%] mx-auto mt-8 text-center text-black/40 font-popinsRegular">
        Resource not found.
      </div>
    );
  }

  const parseSchedule = (raw) => {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  const schedule = parseSchedule(resource.schedule);

  const Field = ({ label, value }) =>
    value ? (
      <div className="mb-5">
        <p className="font-popinsMedium text-sm text-black/50 mb-1">{label}</p>
        <p className="font-popinsRegular text-sm text-black/80 whitespace-pre-wrap">{value}</p>
      </div>
    ) : null;

  // ─── Inquiry handlers ───────────────────────────────────────────────────────

  const fetchInquiries = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingInquiries(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.RESOURCE_INQUIRIES}/${resource.id}`,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } },
      );
      const result = await response.json().catch(() => null);
      setInquiryThreads(response.ok && Array.isArray(result?.data) ? result.data : []);
    } catch {
      setInquiryThreads([]);
    } finally {
      setIsLoadingInquiries(false);
      setInquiriesFetched(true);
    }
  };

  // A thread belongs to either a logged-in PIN user (has userId) or an anonymous
  // guest (has deviceId). Fall back to showing the deviceId when there is no name.
  const inquiryDisplayName = (item) =>
    [item.firstName, item.lastName].filter(Boolean).join(" ") ||
    (item.deviceId ? `Guest · ${item.deviceId}` : "Unknown User");

  const fetchConversation = async (item) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingThread(true);
    try {
      // Logged-in threads are keyed by userId; guest threads by the thread's
      // inquiryId + the owning deviceId (public guest-conversation endpoint).
      const url = item.userId
        ? `${BASE_URL}${API_ENDPOINTS.RESOURCE_INQUIRIES}/${resource.id}/conversation/${item.userId}`
        : `${BASE_URL}${API_ENDPOINTS.RESOURCE_INQUIRIES}/${item.inquiryId}/guest-conversation/${item.deviceId}`;
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => null);
      setThread(response.ok && Array.isArray(result?.data) ? result.data : []);
    } catch {
      setThread([]);
    } finally {
      setIsLoadingThread(false);
    }
  };

  const handleSelectUser = (item) => {
    setSelectedUser({
      userId: item.userId,
      deviceId: item.deviceId,
      inquiryId: item.inquiryId,
      name: inquiryDisplayName(item),
    });
    fetchConversation(item);
  };

  const handleBack = () => {
    setSelectedUser(null);
    setThread([]);
    setReplyMessage("");
  };

  const handleToggleInquiries = () => {
    const next = !showInquiries;
    setShowInquiries(next);
    if (!next) {
      setSelectedUser(null);
      setThread([]);
      setReplyMessage("");
    }
    if (next && !inquiriesFetched) {
      fetchInquiries();
    }
  };

  const handleReloadInquiries = (e) => {
    e.stopPropagation();
    fetchInquiries();
    if (selectedUser) fetchConversation(selectedUser);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedUser) return;
    const token = localStorage.getItem("accessToken");
    if (!token) { toast.error("Please login to reply"); return; }

    setIsSendingReply(true);
    try {
      // Reply via the thread's message endpoint — works for both logged-in and
      // guest threads (the resource owner is a participant of either).
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.RESOURCE_INQUIRIES}/${selectedUser.inquiryId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: replyMessage.trim() }),
        },
      );
      const result = await response.json().catch(() => null);
      if (response.ok && result?.data) {
        // This endpoint returns { isMine } rather than { ownerMessage }. The sender
        // here is always the owner, so tag it so it renders on the owner's side.
        setThread((prev) => [...prev, { ...result.data, ownerMessage: true }]);
        setReplyMessage("");
        setInquiryThreads((prev) =>
          prev.map((t) =>
            t.inquiryId === selectedUser.inquiryId
              ? { ...t, messageCount: t.messageCount + 1, lastMessage: replyMessage.trim(), lastMessageAt: result.data.createdAt }
              : t,
          ),
        );
      } else {
        toast.error(result?.message || "Failed to send reply");
      }
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setIsSendingReply(false);
    }
  };

  return (
    <div className="w-[90%] lg:w-[95%] mx-auto mt-8 mb-10">
      <button
        onClick={() => navigate("/dashboard/manage-resources")}
        className="flex items-center gap-1 text-black/50 hover:text-black/80 font-popinsMedium text-sm mb-6"
      >
        <ArrowLeft size={16} /> Back to Resources
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-popinsBold text-xl lg:text-2xl">{resource.title}</h1>
          <p className="font-popinsRegular text-xs text-black/30 mt-1">
            Added {new Date(resource.createdAt).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={() =>
            navigate(`/dashboard/manage-resources/${resource.id}/edit`, { state: { resource } })
          }
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#3FC4E3] text-white font-popinsMedium text-sm hover:bg-[#006077] transition-colors"
        >
          <Edit size={14} /> Edit
        </button>
      </div>

      <div className="lg:w-[60%] bg-white border border-border rounded-xl p-6">

        {/* Categories */}
        {resource.categories?.length > 0 && (
          <div className="mb-5">
            <p className="font-popinsMedium text-sm text-black/50 mb-2">Categories</p>
            <div className="flex flex-wrap gap-2">
              {resource.categories.map((cat) => (
                <span key={cat.id}
                  className="px-3 py-1 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-full text-xs font-popinsMedium">
                  {cat.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <Field label="Population Served" value={resource.populationServed} />
        <Field label="Income Range" value={resource.incomeRange} />
        <Field label="Description" value={resource.description} />

        {(resource.location || resource.zipCode) && (
          <div className="mb-5">
            <p className="font-popinsMedium text-sm text-black/50 mb-1">Location</p>
            <p className="font-popinsRegular text-sm text-black/80">
              {[resource.location, resource.zipCode].filter(Boolean).join(", ")}
            </p>
          </div>
        )}

        <Field label="Contact Info" value={resource.contactInfo} />

        {schedule.length > 0 && (
          <div className="mb-5">
            <p className="font-popinsMedium text-sm text-black/50 mb-2">Schedule</p>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => {
                const active = schedule.includes(day);
                return (
                  <span key={day}
                    className={`px-3 py-1 rounded-md text-xs font-popinsMedium border ${
                      active
                        ? "bg-[#3FC4E3] border-[#3FC4E3] text-white"
                        : "bg-gray-50 border-border text-black/30"
                    }`}
                  >
                    {day.slice(0, 3)}
                  </span>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* ── Inquiries Section ── */}
      <div className="lg:w-[60%] mt-6">
        <button
          type="button"
          onClick={handleToggleInquiries}
          className="w-full flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-[#3FC4E3]" />
            <h3 className="text-base font-popinsSemiBold">Inquiries</h3>
            {inquiryThreads.length > 0 && (
              <span className="bg-[#3FC4E3] text-white text-xs font-popinsMedium rounded-full px-2 py-0.5">
                {inquiryThreads.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showInquiries && (
              <button
                type="button"
                onClick={handleReloadInquiries}
                disabled={isLoadingInquiries}
                className="p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-40"
              >
                <RefreshCw
                  size={15}
                  className={`text-black/50 ${isLoadingInquiries ? "animate-spin" : ""}`}
                />
              </button>
            )}
            <ChevronDown
              size={20}
              className={`text-black/50 transition-transform ${showInquiries ? "rotate-180" : ""}`}
            />
          </div>
        </button>

        {showInquiries && (
          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
            {isLoadingInquiries ? (
              <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                Loading inquiries...
              </div>
            ) : selectedUser ? (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                  >
                    <ChevronLeft size={18} className="text-black/60" />
                  </button>
                  <span className="font-popinsMedium text-sm text-black/80">
                    {selectedUser.name}
                  </span>
                </div>

                {/* Messages */}
                {isLoadingThread ? (
                  <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                    Loading conversation...
                  </div>
                ) : (
                  <div className="max-h-[240px] overflow-y-auto p-4 space-y-2">
                    {thread.length === 0 ? (
                      <p className="text-center text-black/40 font-popinsRegular text-sm">No messages yet.</p>
                    ) : (
                      thread.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.ownerMessage ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                              msg.ownerMessage
                                ? "bg-[#3FC4E3] text-white rounded-br-sm"
                                : "bg-gray-100 text-black/80 rounded-bl-sm"
                            }`}
                          >
                            <p className="font-popinsRegular text-sm">{msg.message}</p>
                            <p className={`font-popinsRegular text-[10px] mt-0.5 ${msg.ownerMessage ? "text-white/70 text-right" : "text-black/40"}`}>
                              {formatInquiryTime(msg.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Reply input */}
                <div className="flex items-center gap-2 p-3 border-t border-gray-200">
                  <input
                    type="text"
                    placeholder="Type a reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
                    }}
                    disabled={isSendingReply}
                    className="flex-1 font-popinsRegular text-sm rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] transition-all"
                  />
                  <button
                    type="button"
                    onClick={handleSendReply}
                    disabled={isSendingReply || !replyMessage.trim()}
                    className="p-2 rounded-lg bg-[#3FC4E3] text-white hover:bg-[#35b0cc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </>
            ) : inquiryThreads.length === 0 ? (
              <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                No inquiries yet.
              </div>
            ) : (
              /* Thread list */
              <div className="divide-y divide-gray-100">
                {inquiryThreads.map((item) => (
                  <button
                    key={item.inquiryId}
                    type="button"
                    onClick={() => handleSelectUser(item)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#3FC4E3]/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#3FC4E3] font-popinsMedium text-xs">
                          {(inquiryDisplayName(item)[0] || "?").toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-popinsMedium text-sm text-black/80 truncate">
                          {inquiryDisplayName(item)}
                        </p>
                        <p className="font-popinsRegular text-xs text-black/40 truncate">
                          {item.lastMessage}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                      <span className="font-popinsRegular text-[10px] text-black/40">
                        {formatInquiryTime(item.lastMessageAt)}
                      </span>
                      <span className="bg-[#3FC4E3] text-white text-[10px] font-popinsMedium rounded-full px-1.5 py-0.5">
                        {item.messageCount}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
